import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Calendar,
  CalendarCheck,
  CalendarDays,
  CalendarOff,
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  Plus,
  RefreshCw,
  Unlink,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { classifyError, notifyMutationError } from '../lib/errors';

import {
  CalendarEvent,
  connectGoogleCalendar,
  disconnectGoogleCalendar,
  fetchCalendarEvents,
  fetchGoogleCalendarStatus,
  syncGoogleCalendar,
} from '../api/client';
import { queryPersistMeta } from '@/lib/pwa/queryPersistence';
import { EventDetailDialog } from '../components/EventDetailDialog';
import { EventFormDialog, type EventFormDefaultValues } from '../components/EventFormDialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { GoogleIcon } from '@/components/ui/GoogleIcon';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import { queryKeys } from '@/lib/pwa/queryConfig';
import { EmptyState } from '@/components/ui/EmptyState';
import { PageHeader } from '@/components/ui/PageHeader';
import { PautaDeSessoes } from '@/components/calendario/PautaDeSessoes';
import { ComunicacaoSheet } from '../components/comunicacoes/ComunicacaoSheet';
import { useAtuacao } from '@/lib/delegacao/atuacao';
import { FERIADOS_PAGE, type FeriadosPageState } from '@/pages/feriados/rota';


const MONTH_NAMES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

/** Segunda a domingo — a semana do widget Agenda e a da regra do
 *  dia-da-semana (`lib/semana.ts`). Esta tela começava no domingo: eram três
 *  lugares no app com três semanas diferentes. As duas últimas colunas são o
 *  fim de semana, e a grade as marca: em prazo, cair no sábado é fato. */
const WEEK_DAYS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];
const COLUNAS_DE_FIM_DE_SEMANA = 2;

/** A janela da agenda. Sete dias para trás porque prazo perdido ontem ainda
 *  pede ação; seis meses para a frente porque "a seguir" sem teto seria uma
 *  consulta sem fim, e uma pauta de sessão raramente é marcada além disso. */
const DIAS_DE_PASSADO = 7;
const DIAS_DE_FUTURO = 180;
/** O teto da rota (`MAX_PAGE_SIZE`). Com o padrão de 100, a janela de seis
 *  meses perdia o fim da agenda calada. */
const LIMITE_DA_AGENDA = 200;

type ModoAgenda = 'seguir' | 'passados' | 'dia';

/** Extrai data inicial do query param ?data=YYYY-MM-DD (lido uma vez no mount). */
function getInitialDateFromParams(): Date | null {
  const params = new URLSearchParams(window.location.search);
  const data = params.get('data');
  if (data) {
    const parsed = parseISO(data);
    if (!isNaN(parsed.getTime())) return parsed;
  }
  return null;
}

function CalendarPage() {
  const queryClient = useQueryClient();
  const [currentDate, setCurrentDate] = useState(() => {
    const d = getInitialDateFromParams();
    return d ? new Date(d.getFullYear(), d.getMonth(), 1) : new Date();
  });
  const [selectedDate, setSelectedDate] = useState<Date | null>(() => getInitialDateFromParams());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);
  const [eventDefaults, setEventDefaults] = useState<EventFormDefaultValues | undefined>();
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const [comunicacaoSheetId, setComunicacaoSheetId] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  // Que pergunta a agenda está respondendo. Antes eram três listas na mesma
  // tela — o painel do dia, "Próximos 15 dias" e "Últimos 7 dias" —, cada uma
  // com sua regra e sua consulta.
  // Chegando com ?data= ("ver no calendário" da intimação), a agenda abre naquele dia.
  const [modoAgenda, setModoAgenda] = useState<ModoAgenda>(() => (getInitialDateFromParams() ? 'dia' : 'seguir'));
  const [searchParams, setSearchParams] = useSearchParams();
  const autoSyncDone = useRef(false);

  // Limpar query param ?data após leitura inicial
  useEffect(() => {
    if (searchParams.has('data')) {
      searchParams.delete('data');
      setSearchParams(searchParams, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0, 23, 59, 59);

  const { data: events = [], isLoading, error: eventsError, dataUpdatedAt } = useQuery({
    queryKey: [...queryKeys.calendario.eventos, startOfMonth.toISOString(), endOfMonth.toISOString()],
    queryFn: () => fetchCalendarEvents(startOfMonth.toISOString(), endOfMonth.toISOString()),
    meta: queryPersistMeta.nonEmpty,
  });

  // Google Calendar é integração da pessoa do titular: fora da conta dele.
  const atuacao = useAtuacao();
  const navigate = useNavigate();
  const semAcoesProprias = !!atuacao;
  const { data: googleStatus, error: googleError } = useQuery({
    queryKey: [...queryKeys.calendario.googleStatus],
    queryFn: fetchGoogleCalendarStatus,
    meta: queryPersistMeta.never,
    enabled: !semAcoesProprias,
  });

  // Sync manual (via botão) — exibe toast de sucesso
  const syncMutation = useMutation({
    mutationFn: syncGoogleCalendar,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...queryKeys.calendario.eventos] });
      toast.success('Calendário sincronizado com sucesso');
    },
    onError: (err) => notifyMutationError(err, toast.error),
  });

  // Sync silencioso (ao abrir a página) — sem toast
  const silentSyncMutation = useMutation({
    mutationFn: syncGoogleCalendar,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...queryKeys.calendario.eventos] });
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: disconnectGoogleCalendar,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...queryKeys.calendario.googleStatus] });
      toast.success('Google Calendar desconectado');
      setDisconnectDialogOpen(false);
    },
  });

  // Auto-sync ao abrir a página (se Google conectado)
  useEffect(() => {
    if (!googleStatus?.connected || autoSyncDone.current) return;
    autoSyncDone.current = true;

    // Limpa query string do redirect OAuth, se presente
    if (searchParams.get('google') === 'connected') {
      setSearchParams({}, { replace: true });
    }

    silentSyncMutation.mutate();
  }, [googleStatus?.connected, searchParams, setSearchParams, silentSyncMutation]);

  const daysInMonth = endOfMonth.getDate();
  // Segunda = 0. `getDay()` conta a partir do domingo, e a grade começa na
  // segunda — sem o deslocamento, o mês inteiro sai um dia fora do lugar.
  const firstDayOfMonth = (startOfMonth.getDay() + 6) % 7;
  const today = new Date();

  const eventsByDay = useMemo(() => {
    const map = new Map<number, CalendarEvent[]>();
    const monthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0, 23, 59, 59, 999);
    events.forEach((event) => {
      const inicio = parseISO(event.data_inicio);
      const fim = event.data_fim ? parseISO(event.data_fim) : inicio;
      // Recortar o range para o mês visualizado (eventos multi-day cruzam meses).
      const rangeStart = inicio < monthStart ? monthStart : inicio;
      const rangeEnd = fim > monthEnd ? monthEnd : fim;
      if (rangeEnd < monthStart || rangeStart > monthEnd) return;
      for (let day = rangeStart.getDate(); day <= rangeEnd.getDate(); day++) {
        if (!map.has(day)) {
          map.set(day, []);
        }
        map.get(day)!.push(event);
      }
    });
    return map;
  }, [events, currentDate]);

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date());
    setModoAgenda('dia');
  };

  const handleDayClick = (day: number) => {
    setModoAgenda('dia');
    const clickedDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    setSelectedDate(clickedDate);
  };

  const handleOpenNewEvent = (date?: Date) => {
    const startDate = date || selectedDate || new Date();
    setEditingEvent(null);
    setEventDefaults({ data_inicio: startDate.toISOString() });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingEvent(null);
    setEventDefaults(undefined);
  };

  const handleConnectGoogle = useCallback(async () => {
    try {
      const { auth_url } = await connectGoogleCalendar();
      window.location.href = auth_url;
    } catch (err) {
      notifyMutationError(err, toast.error);
    }
  }, []);

  // ── A agenda ──────────────────────────────────────────────────────────
  // Uma consulta só, numa janela que cobre os três modos. A da grade é do
  // mês visível e não alcança nem o passado recente nem o mês seguinte.
  // A janela é calculada na hora da busca, e a chave não leva o dia: aberta
  // offline no dia seguinte, a tela usa a agenda em cache em vez de uma chave
  // nova e vazia que diria "nenhum compromisso pela frente".
  const { data: eventosDaAgenda = [], isLoading: carregandoAgenda, error: erroAgenda } = useQuery({
    queryKey: [...queryKeys.calendario.eventos, 'agenda'],
    queryFn: () => {
      const agora = new Date();
      const inicio = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate() - DIAS_DE_PASSADO);
      const fim = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate() + DIAS_DE_FUTURO, 23, 59, 59, 999);
      return fetchCalendarEvents(inicio.toISOString(), fim.toISOString(), undefined, LIMITE_DA_AGENDA);
    },
    meta: queryPersistMeta.nonEmpty,
  });

  const { aSeguir, passados } = useMemo(() => {
    const agora = new Date();
    const hoje = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate());
    const limite = new Date(hoje);
    limite.setDate(hoje.getDate() - DIAS_DE_PASSADO);
    const comeco = (e: CalendarEvent) => parseISO(e.data_inicio);
    // O fim decide se o compromisso já passou: um evento de vários dias que
    // começou ontem e termina amanhã ainda é "a seguir".
    const fim = (e: CalendarEvent) => (e.data_fim ? parseISO(e.data_fim) : comeco(e));

    return {
      aSeguir: eventosDaAgenda
        .filter((e) => fim(e) >= hoje)
        .sort((a, b) => comeco(a).getTime() - comeco(b).getTime()),
      passados: eventosDaAgenda
        .filter((e) => fim(e) < hoje && fim(e) >= limite)
        .sort((a, b) => comeco(b).getTime() - comeco(a).getTime()),
    };
  }, [eventosDaAgenda]);

  const selectedDayEvents = useMemo(() => {
    if (!selectedDate) return [];
    // Eventos multi-day aparecem em todos os dias do range (não só no início).
    const selectedDayStart = new Date(selectedDate);
    selectedDayStart.setHours(0, 0, 0, 0);
    const selectedDayEnd = new Date(selectedDate);
    selectedDayEnd.setHours(23, 59, 59, 999);
    // As duas consultas: trocar o mês da grade não esvazia o dia escolhido.
    const porId = new Map<string, CalendarEvent>();
    for (const event of [...eventosDaAgenda, ...events]) porId.set(event.id, event);
    return [...porId.values()]
      .filter((event) => {
        const inicio = parseISO(event.data_inicio);
        const fim = event.data_fim ? parseISO(event.data_fim) : inicio;
        return inicio <= selectedDayEnd && fim >= selectedDayStart;
      })
      .sort((a, b) => a.data_inicio.localeCompare(b.data_inicio));
  }, [events, eventosDaAgenda, selectedDate]);

  const isTodayDay = (day: number) =>
    today.getDate() === day &&
    today.getMonth() === currentDate.getMonth() &&
    today.getFullYear() === currentDate.getFullYear();

  /** O que a agenda mostra AGORA, conforme o modo. */
  const eventosDaAgendaVisivel = useMemo(() => {
    if (modoAgenda === 'passados') return passados;
    if (modoAgenda === 'dia') return selectedDayEvents;
    return aSeguir;
  }, [modoAgenda, passados, selectedDayEvents, aSeguir]);

  const isSelected = (day: number) =>
    selectedDate &&
    selectedDate.getDate() === day &&
    selectedDate.getMonth() === currentDate.getMonth() &&
    selectedDate.getFullYear() === currentDate.getFullYear();

  return (
    <div className="space-y-4">
      {/* Sem subtítulo: "Gerencie seus compromissos e prazos" explica o que a
          pessoa acabou de escolher no menu, destacado ao lado. */}
      <PageHeader
        icon={<Calendar />}
        title="Calendário"
        dataUpdatedAt={dataUpdatedAt}
        actions={<>
          {/* Os feriados e suspensões da conta: também na delegação (a página respeita o nível). */}
          <Button
            variant="outline"
            className="min-h-11 min-w-11 sm:min-h-0 sm:min-w-0"
            onClick={() => navigate(FERIADOS_PAGE, { state: { voltarPara: '/app/calendario' } satisfies FeriadosPageState })}
            aria-label="Feriados e suspensões"
            title="Feriados e suspensões"
          >
            <CalendarOff className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Feriados e suspensões</span>
          </Button>
          {/* Na conta de outra pessoa, sem as ações do Google Calendar (integração do titular). */}
          {!semAcoesProprias && <>
            {/* Google Calendar actions — desktop */}
            {googleStatus?.connected ? (
              <div className="hidden sm:flex items-center gap-2">
                <Button
                  variant="outline"
                  className="border-emerald-500/50 text-emerald-600 hover:bg-emerald-500/10 dark:text-emerald-400"
                  onClick={() => setDisconnectDialogOpen(true)}
                >
                  <GoogleIcon className="mr-2 h-4 w-4" />
                  Google Calendar conectado
                </Button>
                <Button
                  variant="outline"
                  onClick={() => syncMutation.mutate()}
                  disabled={syncMutation.isPending}
                >
                  <RefreshCw className={cn('mr-2 h-4 w-4', syncMutation.isPending && 'animate-spin')} />
                  Sincronizar
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                className="hidden sm:inline-flex"
                onClick={handleConnectGoogle}
              >
                <GoogleIcon className="mr-2 h-4 w-4" />
                Conectar Google Calendar
              </Button>
            )}

            {/* Google Calendar actions — mobile (DropdownMenu) */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-11 w-11 sm:hidden" aria-label="Google Calendar">
                  <MoreVertical className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {googleStatus?.connected ? (
                  <>
                    <DropdownMenuItem onClick={() => syncMutation.mutate()} disabled={syncMutation.isPending}>
                      <RefreshCw className={cn('mr-2 h-4 w-4', syncMutation.isPending && 'animate-spin')} />
                      Sincronizar Google
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      onClick={() => setDisconnectDialogOpen(true)}
                    >
                      <Unlink className="mr-2 h-4 w-4" />
                      Desconectar Google
                    </DropdownMenuItem>
                  </>
                ) : (
                  <DropdownMenuItem onClick={handleConnectGoogle}>
                    <GoogleIcon className="mr-2 h-4 w-4" />
                    Conectar Google Calendar
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Novo Evento */}
            <Button onClick={() => handleOpenNewEvent()} aria-label="Novo evento" className="h-11 w-11 p-0 sm:h-9 sm:w-auto sm:px-4">
              <Plus className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Novo Evento</span>
            </Button>
          </>}
        </>}
      />

      {(eventsError || erroAgenda || googleError) && (
        <Alert variant="destructive">
          <AlertDescription>{classifyError(eventsError || erroAgenda || googleError).message}</AlertDescription>
        </Alert>
      )}

      {/* Uma faixa só: o mês à esquerda como SELETOR, a agenda à direita como
          conteúdo. Antes eram a grade (515px, para marcar 5 dias de 30), o
          painel do dia e mais dois cards de resumo — 1102px de página para
          423 visíveis, com a única lista que responde "o que tenho pela
          frente" no rodapé. */}
      <Card>
        <CardContent className="flex flex-col gap-5 p-4 sm:p-5 lg:flex-row">

          {/* ── O mês: um seletor de dia ────────────────────────────────── */}
          <div className="flex w-full flex-col gap-3 lg:w-[300px] lg:shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" className="h-11 w-11 sm:h-8 sm:w-8" onClick={prevMonth} aria-label="Mês anterior">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <h2 className="min-w-[128px] text-center text-sm font-semibold">
                  {MONTH_NAMES[currentDate.getMonth()]} {currentDate.getFullYear()}
                </h2>
                <Button variant="ghost" size="icon" className="h-11 w-11 sm:h-8 sm:w-8" onClick={nextMonth} aria-label="Próximo mês">
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              <Button variant="ghost" size="sm" className="h-11 px-2 text-primary sm:h-8" onClick={goToToday}>
                <CalendarCheck className="mr-1.5 h-4 w-4" />
                Hoje
              </Button>
            </div>

            <div className="grid grid-cols-7">
              {WEEK_DAYS.map((nome, coluna) => (
                <span
                  key={nome}
                  className={cn(
                    'pb-1 text-center text-xs font-semibold',
                    coluna >= WEEK_DAYS.length - COLUNAS_DE_FIM_DE_SEMANA
                      ? 'text-muted-foreground/60'
                      : 'text-muted-foreground',
                  )}
                >
                  {nome}
                </span>
              ))}
            </div>

            {/* Skeleton: exceção por layout shift */}
            {isLoading ? (
              <div className="grid grid-cols-7">
                {Array.from({ length: 35 }).map((_, i) => (
                  <Skeleton key={i} className="h-11 rounded-none" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-7 overflow-hidden rounded-lg">
                {Array.from({ length: firstDayOfMonth }).map((_, i) => (
                  <div
                    key={`vazio-${i}`}
                    className={cn('h-11', i >= WEEK_DAYS.length - COLUNAS_DE_FIM_DE_SEMANA && 'bg-muted/40')}
                  />
                ))}
                {Array.from({ length: daysInMonth }).map((_, i) => {
                  const day = i + 1;
                  const dayEvents = eventsByDay.get(day) || [];
                  const todayDay = isTodayDay(day);
                  const selectedDay = isSelected(day);
                  const coluna = (firstDayOfMonth + i) % 7;
                  const fimDeSemana = coluna >= WEEK_DAYS.length - COLUNAS_DE_FIM_DE_SEMANA;

                  return (
                    <Button
                      key={day}
                      type="button"
                      variant="unstyled"
                      onClick={() => handleDayClick(day)}
                      aria-pressed={!!selectedDay}
                      aria-label={
                        dayEvents.length === 0
                          ? `${day} de ${MONTH_NAMES[currentDate.getMonth()]}, sem compromisso`
                          : `${day} de ${MONTH_NAMES[currentDate.getMonth()]}, ${dayEvents.length} ${dayEvents.length === 1 ? 'compromisso' : 'compromissos'}`
                      }
                      className={cn(
                        'flex h-11 items-center justify-center rounded-none p-0 font-normal transition-colors hover:bg-primary/5',
                        // Fim de semana com fundo próprio: num calendário de
                        // prazos, "cai no sábado" muda o que se faz.
                        fimDeSemana && 'bg-muted/40',
                      )}
                    >
                      {/* Sem as 31 caixas com borda: a malha pesava mais que o
                          conteúdo. O disco diz "tem algo aqui"; o disco cheio,
                          "é hoje"; o anel, "é o dia que você abriu". */}
                      <span
                        className={cn(
                          'flex h-[30px] w-[30px] items-center justify-center rounded-full text-sm leading-none',
                          todayDay
                            ? 'bg-primary font-bold text-primary-foreground'
                            : dayEvents.length > 0
                              ? 'bg-primary/10 font-semibold text-primary'
                              : fimDeSemana
                                ? 'text-muted-foreground'
                                : '',
                          selectedDay && 'ring-2 ring-primary ring-offset-2 ring-offset-background',
                        )}
                      >
                        {day}
                      </span>
                    </Button>
                  );
                })}
              </div>
            )}

          </div>

          {/* ── A agenda: o que a tela existe para responder ─────────────── */}
          <div className="flex min-w-0 flex-1 flex-col gap-3 lg:border-l lg:border-border lg:pl-5">
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant={modoAgenda === 'seguir' ? 'default' : 'outline'}
                size="sm"
                className="h-11 rounded-full sm:h-8"
                aria-pressed={modoAgenda === 'seguir'}
                onClick={() => { setModoAgenda('seguir'); setSelectedDate(null); }}
              >
                A seguir ({aSeguir.length})
              </Button>
              <Button
                variant={modoAgenda === 'passados' ? 'default' : 'outline'}
                size="sm"
                className="h-11 rounded-full sm:h-8"
                aria-pressed={modoAgenda === 'passados'}
                onClick={() => { setModoAgenda('passados'); setSelectedDate(null); }}
              >
                Últimos {DIAS_DE_PASSADO} dias ({passados.length})
              </Button>
              {modoAgenda === 'dia' && selectedDate && (
                <span className="inline-flex h-11 items-center gap-1 rounded-full bg-primary pl-3 pr-1 text-xs font-semibold text-primary-foreground sm:h-8">
                  {format(selectedDate, 'dd/MM')}
                  <Button
                    type="button"
                    variant="unstyled"
                    aria-label="Voltar para o que vem a seguir"
                    className="h-9 w-9 rounded-full p-0 hover:bg-primary-foreground/20 sm:h-6 sm:w-6"
                    onClick={() => { setModoAgenda('seguir'); setSelectedDate(null); }}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </span>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto h-11 text-primary sm:h-8"
                onClick={() => handleOpenNewEvent(selectedDate ?? undefined)}
              >
                <Plus className="mr-1 h-4 w-4" />
                {selectedDate ? `Marcar em ${format(selectedDate, 'dd/MM')}` : 'Marcar compromisso'}
              </Button>
            </div>

            {carregandoAgenda ? (
              <div className="flex justify-center py-10">
                <Spinner size={32} label="Carregando compromissos" />
              </div>
            ) : erroAgenda && eventosDaAgendaVisivel.length === 0 ? null : eventosDaAgendaVisivel.length === 0 ? (
              <EmptyState
                icon={<CalendarDays />}
                title={
                  modoAgenda === 'passados'
                    ? `Nenhum compromisso nos últimos ${DIAS_DE_PASSADO} dias`
                    : modoAgenda === 'dia' && selectedDate
                      ? `Nada marcado em ${format(selectedDate, "d 'de' MMMM", { locale: ptBR })}`
                      : 'Nenhum compromisso pela frente'
                }
                // Sem ação própria: o "Marcar compromisso" da linha de cima já está à vista.
              />
            ) : (
              // A agenda como PAUTA: sessão de julgamento numa linha, aberta
              // em relator e processos; o que não é sessão (audiência,
              // reunião, Google) em linha própria.
              <div className="lg:max-h-[22rem] lg:overflow-y-auto lg:pr-1">
                <PautaDeSessoes
                  events={eventosDaAgendaVisivel}
                  onSelectEvent={setSelectedEvent}
                  recentesPrimeiro={modoAgenda === 'passados'}
                />
              </div>
            )}
          </div>
        </CardContent>
      </Card>


      {/* A confirmação padrão, e não um diálogo montado à mão: este era o
          último modal da tela fora do padrão. */}
      <ConfirmDialog
        open={disconnectDialogOpen}
        title="Desconectar o Google Calendar"
        message="Os eventos já sincronizados ficam onde estão. Novas sincronizações param até você reconectar a conta."
        confirmLabel="Desconectar"
        confirmColor="error"
        loading={disconnectMutation.isPending}
        onConfirm={() => disconnectMutation.mutate()}
        onCancel={() => setDisconnectDialogOpen(false)}
      />

      <EventFormDialog
        open={isModalOpen}
        onClose={handleCloseModal}
        event={editingEvent}
        defaultValues={eventDefaults}
      />

      <EventDetailDialog
        event={selectedEvent}
        onClose={() => setSelectedEvent(null)}
        onOpenComunicacao={setComunicacaoSheetId}
      />

      <ComunicacaoSheet
        comunicacaoId={comunicacaoSheetId}
        onClose={() => setComunicacaoSheetId(null)}
      />
    </div>
  );
}

export default CalendarPage;
